import { FEATURE_FLAG_DOMAIN, resolveFlagGate } from '@heidi/runtime/feature-flags';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';

const RUST_AUTH0_TOKEN_REFRESH_DESKTOP_FLAG_KEY =
  'release-apps-rust-auth0-token-refresh-desktop';

/**
 * On desktop mid-session 401, refresh Auth0 in Rust instead of parking on an
 * FE `token_refresh_needed` round-trip. Off by default; locked at session start.
 *
 * Lives in a sibling `.ts` module (not `flags/index.tsx`) so the React Refresh
 * ratchet does not count these non-component exports against the barrel.
 */
export const useIsAppsRustAuth0TokenRefreshDesktopEnabled = () => {
  return useBooleanFlagValue(RUST_AUTH0_TOKEN_REFRESH_DESKTOP_FLAG_KEY, false);
};

/**
 * Non-hook variant of {@link useIsAppsRustAuth0TokenRefreshDesktopEnabled} for
 * call sites that run outside React — e.g. the RPA recorder upload auth-context
 * builder (`buildDefaultUploadAuthContext`), which reads the current auth token
 * imperatively from the store rather than via hooks.
 */
export const isAppsRustAuth0TokenRefreshDesktopEnabled = () =>
  OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    RUST_AUTH0_TOKEN_REFRESH_DESKTOP_FLAG_KEY,
    false
  );

/**
 * Boot-time variant: waits (bounded) for the flag provider to reach READY
 * instead of reading the code default as a real OFF. Both the hook and the
 * sync getter above return `false` until Flagsmith hydrates, which is still
 * pending on the app root's first commit — so a post-reload re-attach that read
 * either one would lock `rustAuth0TokenRefresh: false` for a session Rust
 * started with it ON, re-introducing the double refresh APP-8801 removed.
 */
export const resolveIsAppsRustAuth0TokenRefreshDesktopEnabled = async (
  timeoutMs = 2000
) =>
  (await resolveFlagGate(RUST_AUTH0_TOKEN_REFRESH_DESKTOP_FLAG_KEY, timeoutMs)) === 'on';
